import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ROL_CONFIG = {
  superadmin: { label: 'Super Admin',   color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: '👑' },
  admin:      { label: 'Administrador', color: '#00E5FF', bg: 'rgba(0,229,255,0.1)', icon: '🗂' },
  tecnico:    { label: 'Técnico',       color: '#6366F1', bg: 'rgba(99,102,241,0.1)', icon: '🔧' },
};

const MODULOS_LABEL = {
  dashboard: 'Dashboard', clientes: 'Clientes', planes: 'Planes',
  cartera: 'Cartera', transacciones: 'Ingresos/Egresos', ventas: 'Ventas',
  reportes: 'Reportes', tickets: 'Tickets', agenda: 'Agenda',
  equipos: 'Equipos', caja: 'Caja diaria', configuracion: 'Configuración', usuarios: 'Usuarios',
};

const ACCESO_POR_ROL = {
  superadmin: Object.keys(MODULOS_LABEL),
  admin:      Object.keys(MODULOS_LABEL).filter(m => !['mikrotik','usuarios','equipos_red'].includes(m)),
  tecnico:    ['dashboard','clientes','tickets','agenda','equipos'],
};

const formVacio = { nombre: '', email: '', password: '', rol: 'admin', activo: true };

export default function Usuarios() {
  const { usuario: usuarioActual } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(formVacio);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [tabRol, setTabRol] = useState('todos');

  const cargar = useCallback(async () => {
    const { data } = await api.get('/api/usuarios');
    setUsuarios(data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => { setEditando(null); setForm(formVacio); setError(''); setModal(true); };
  const abrirEditar = (u) => {
    setEditando(u);
    setForm({ nombre: u.nombre, email: u.email, password: '', rol: u.rol, activo: u.activo });
    setError(''); setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.email) { setError('Nombre y email son requeridos'); return; }
    if (!editando && !form.password) { setError('La contraseña es requerida para usuarios nuevos'); return; }
    setGuardando(true);
    try {
      const datos = { ...form };
      if (!datos.password) delete datos.password;
      if (editando) await api.put(`/api/usuarios/${editando.id}`, datos);
      else await api.post('/api/usuarios', datos);
      setModal(false);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const toggleActivo = async (u) => {
    if (u.id === usuarioActual.id) { alert('No puedes desactivarte a ti mismo'); return; }
    try {
      await api.put(`/api/usuarios/${u.id}`, { activo: !u.activo });
      cargar();
    } catch (err) { alert('Error al cambiar estado'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const usuariosFiltrados = tabRol === 'todos' ? usuarios : usuarios.filter(u => u.rol === tabRol);

  return (
    <Layout>
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Usuarios</h1>
          <p style={s.sub}>Gestión de accesos y roles del sistema</p>
        </div>
        <button style={s.btnPrimario} onClick={abrirNuevo}>+ Nuevo Usuario</button>
      </div>

      {/* Stats por rol */}
      <div style={s.statsRow}>
        {Object.entries(ROL_CONFIG).map(([rol, cfg]) => {
          const count = usuarios.filter(u => u.rol === rol && u.activo).length;
          return (
            <div key={rol} style={s.statCard}>
              <span style={{ fontSize: '24px' }}>{cfg.icon}</span>
              <span style={{ ...s.statVal, color: cfg.color }}>{count}</span>
              <span style={s.statLabel}>{cfg.label}</span>
            </div>
          );
        })}
        <div style={s.statCard}>
          <span style={{ fontSize: '24px' }}>👥</span>
          <span style={{ ...s.statVal, color: '#fff' }}>{usuarios.filter(u => u.activo).length}</span>
          <span style={s.statLabel}>Total activos</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {[['todos','Todos'], ['superadmin','Super Admin'], ['admin','Administradores'], ['tecnico','Técnicos']].map(([val, label]) => (
          <button key={val} onClick={() => setTabRol(val)}
            style={{ ...s.tab, ...(tabRol === val ? s.tabActivo : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {/* Tabla usuarios */}
      <div style={s.tabla}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>Usuario</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Rol</th>
              <th style={s.th}>Acceso a módulos</th>
              <th style={s.th}>Estado</th>
              <th style={s.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados.length === 0 ? (
              <tr><td colSpan={6} style={s.vacio}>No hay usuarios en esta categoría</td></tr>
            ) : usuariosFiltrados.map(u => {
              const cfg = ROL_CONFIG[u.rol] || ROL_CONFIG.tecnico;
              const modulos = ACCESO_POR_ROL[u.rol] || [];
              return (
                <tr key={u.id} style={{ ...s.tr, opacity: u.activo ? 1 : 0.5 }}>
                  <td style={s.td}>
                    <div style={s.usuarioRow}>
                      <div style={{ ...s.avatar, background: cfg.bg, color: cfg.color }}>{cfg.icon}</div>
                      <div>
                        <div style={s.usuarioNombre}>{u.nombre}</div>
                        {u.id === usuarioActual.id && <span style={s.tuTag}>Tú</span>}
                      </div>
                    </div>
                  </td>
                  <td style={s.td}><span style={s.email}>{u.email}</span></td>
                  <td style={s.td}>
                    <span style={{ ...s.rolBadge, color: cfg.color, background: cfg.bg }}>
                      {cfg.icon} {cfg.label}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={s.modulosRow}>
                      {modulos.slice(0, 5).map(m => (
                        <span key={m} style={s.moduloTag}>{MODULOS_LABEL[m]}</span>
                      ))}
                      {modulos.length > 5 && <span style={s.moduloMas}>+{modulos.length - 5}</span>}
                    </div>
                  </td>
                  <td style={s.td}>
                    <span style={{ ...s.estadoBadge, color: u.activo ? '#10B981' : '#6B7280', background: u.activo ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)' }}>
                      {u.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={s.acciones}>
                      <button style={s.btnEdit} onClick={() => abrirEditar(u)}>Editar</button>
                      {u.id !== usuarioActual.id && (
                        <button style={u.activo ? s.btnDesact : s.btnAct} onClick={() => toggleActivo(u)}>
                          {u.activo ? 'Desactivar' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Panel de permisos por rol */}
      <div style={s.permisosPanel}>
        <h3 style={s.permisosTitulo}>📋 Resumen de accesos por rol</h3>
        <div style={s.permisosGrid}>
          {Object.entries(ROL_CONFIG).map(([rol, cfg]) => (
            <div key={rol} style={s.permisoCard}>
              <div style={s.permisoHeader}>
                <span>{cfg.icon}</span>
                <span style={{ ...s.permisoRol, color: cfg.color }}>{cfg.label}</span>
              </div>
              <div style={s.permisoModulos}>
                {Object.keys(MODULOS_LABEL).map(mod => {
                  const tiene = ACCESO_POR_ROL[rol]?.includes(mod);
                  return (
                    <div key={mod} style={s.permisoItem}>
                      <span style={{ color: tiene ? '#10B981' : 'rgba(255,255,255,0.2)' }}>
                        {tiene ? '✓' : '✗'}
                      </span>
                      <span style={{ color: tiene ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)', fontSize: '12px' }}>
                        {MODULOS_LABEL[mod]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal crear/editar */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar Usuario' : 'Nuevo Usuario'}>
        <div style={s.formGrid}>
          <div style={{ ...s.campo, gridColumn: '1/-1' }}>
            <label style={s.label}>Nombre completo *</label>
            <input style={s.input} value={form.nombre} onChange={e => f('nombre', e.target.value)} placeholder="Nombre del usuario" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Correo electrónico *</label>
            <input style={s.input} type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="correo@empresa.com" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Rol *</label>
            <select style={s.input} value={form.rol} onChange={e => f('rol', e.target.value)}>
              <option value="superadmin">👑 Super Admin</option>
              <option value="admin">🗂 Administrador</option>
              <option value="tecnico">🔧 Técnico</option>
            </select>
          </div>
          <div style={{ ...s.campo, gridColumn: '1/-1' }}>
            <label style={s.label}>{editando ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
            <input style={s.input} type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder={editando ? 'Dejar vacío para mantener la actual' : 'Mínimo 8 caracteres'} />
          </div>

          {/* Preview de accesos según rol */}
          <div style={{ ...s.campo, gridColumn: '1/-1' }}>
            <label style={s.label}>Módulos con acceso según rol seleccionado</label>
            <div style={s.modulosPreview}>
              {Object.keys(MODULOS_LABEL).map(mod => {
                const tiene = ACCESO_POR_ROL[form.rol]?.includes(mod);
                return (
                  <span key={mod} style={{ ...s.moduloPreviewItem, color: tiene ? '#10B981' : 'rgba(255,255,255,0.25)', background: tiene ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.03)' }}>
                    {tiene ? '✓' : '✗'} {MODULOS_LABEL[mod]}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => setModal(false)}>Cancelar</button>
          <button style={s.btnPrimario} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </div>
      </Modal>
    </Layout>
  );
}

const s = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'12px', marginBottom:'24px' },
  titulo: { fontSize:'24px', fontWeight:'700', marginBottom:'4px' },
  sub: { color:'rgba(255,255,255,0.4)', fontSize:'14px' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnSec: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 18px', color:'#fff', fontSize:'14px', cursor:'pointer' },
  statsRow: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:'12px', marginBottom:'20px' },
  statCard: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', padding:'16px', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' },
  statVal: { fontSize:'28px', fontWeight:'700' },
  statLabel: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  tabs: { display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'16px', borderBottom:'1px solid rgba(255,255,255,0.08)', paddingBottom:'0' },
  tab: { background:'none', border:'none', borderBottom:'2px solid transparent', padding:'10px 16px', color:'rgba(255,255,255,0.4)', fontSize:'13px', cursor:'pointer', marginBottom:'-1px' },
  tabActivo: { color:'#00E5FF', borderBottomColor:'#00E5FF' },
  tabla: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', overflow:'hidden', marginBottom:'20px' },
  table: { width:'100%', borderCollapse:'collapse' },
  thead: { background:'rgba(255,255,255,0.04)' },
  th: { padding:'12px 16px', textAlign:'left', fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'600', letterSpacing:'0.5px', textTransform:'uppercase' },
  tr: { borderTop:'1px solid rgba(255,255,255,0.05)' },
  td: { padding:'12px 16px', verticalAlign:'middle' },
  vacio: { padding:'40px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'14px' },
  usuarioRow: { display:'flex', alignItems:'center', gap:'10px' },
  avatar: { width:'36px', height:'36px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 },
  usuarioNombre: { fontSize:'14px', fontWeight:'600' },
  tuTag: { fontSize:'10px', background:'rgba(0,229,255,0.1)', color:'#00E5FF', padding:'1px 6px', borderRadius:'10px' },
  email: { fontSize:'12px', color:'rgba(255,255,255,0.5)' },
  rolBadge: { fontSize:'12px', fontWeight:'600', padding:'4px 10px', borderRadius:'20px' },
  modulosRow: { display:'flex', flexWrap:'wrap', gap:'4px' },
  moduloTag: { fontSize:'10px', background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.5)', padding:'2px 7px', borderRadius:'10px' },
  moduloMas: { fontSize:'10px', color:'rgba(255,255,255,0.3)', padding:'2px 6px' },
  estadoBadge: { fontSize:'12px', fontWeight:'600', padding:'3px 10px', borderRadius:'20px' },
  acciones: { display:'flex', gap:'6px' },
  btnEdit: { background:'rgba(99,102,241,0.15)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#A5B4FC', fontSize:'12px', cursor:'pointer' },
  btnDesact: { background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
  btnAct: { background:'rgba(16,185,129,0.1)', border:'none', borderRadius:'6px', padding:'5px 10px', color:'#6EE7B7', fontSize:'12px', cursor:'pointer' },
  permisosPanel: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'16px', padding:'20px' },
  permisosTitulo: { fontSize:'14px', fontWeight:'600', marginBottom:'16px', color:'rgba(255,255,255,0.8)' },
  permisosGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap:'16px' },
  permisoCard: { background:'rgba(255,255,255,0.03)', borderRadius:'10px', padding:'14px' },
  permisoHeader: { display:'flex', alignItems:'center', gap:'8px', marginBottom:'12px' },
  permisoRol: { fontSize:'13px', fontWeight:'700' },
  permisoModulos: { display:'flex', flexDirection:'column', gap:'6px' },
  permisoItem: { display:'flex', alignItems:'center', gap:'6px' },
  formGrid: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap:'14px', marginBottom:'16px' },
  campo: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { color:'rgba(255,255,255,0.6)', fontSize:'13px' },
  input: { width:'100%', minWidth:0, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'10px 14px', color:'#fff', fontSize:'14px', outline:'none' },
  modulosPreview: { display:'flex', flexWrap:'wrap', gap:'6px', padding:'10px', background:'rgba(255,255,255,0.03)', borderRadius:'8px' },
  moduloPreviewItem: { fontSize:'11px', padding:'3px 8px', borderRadius:'10px', fontWeight:'500' },
  error: { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px', marginBottom:'16px' },
  modalFooter: { display:'flex', gap:'12px', justifyContent:'flex-end' },
};
